// src/features/records/hooks/useRecordList.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useEntityCache from '../stores/entityCache';
import { fetchRecordsByIds } from '../services/core/queries';
import { searchEdges } from '../services/edges/search';
import { subscribeEdges } from '../services/edges/subscribe';
import useUser from '../../../hooks/useUser';

function timestampToMillis(value) {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value === 'object' && value !== null && typeof value.seconds === 'number') {
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
    return value.seconds * 1000 + Math.floor(nanos / 1e6);
  }
  return null;
}

function priorityNumberToLabel(value) {
  if (value === 1) return 'high';
  if (value === 2) return 'medium';
  if (value === 3) return 'low';
  return undefined;
}

function normalizePriority(value) {
  if (typeof value === 'string') {
    return value;
  }
  return priorityNumberToLabel(typeof value === 'number' ? value : undefined);
}

function buildDateAttributeFromQuery(query) {
  if (!query?.startAt && !query?.endAt) {
    return null;
  }

  const date = {};
  if (query.startAt) {
    date.start = query.startAt;
  }
  if (query.endAt) {
    date.end = query.endAt;
  }

  return Object.keys(date).length ? date : null;
}

function buildLocationAttributeFromQuery(query) {
  const loc = query?.loc;
  if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') {
    return null;
  }

  const location = {
    lat: loc.lat,
    lng: loc.lng
  };

  if (loc.geohash) {
    location.geohash = loc.geohash;
  }

  return location;
}

function stableSerialize(value) {
  if (value === null) {
    return null;
  }

  if (typeof value === 'bigint') {
    return { __type: 'BigInt', value: value.toString() };
  }

  if (typeof value !== 'object') {
    return value;
  }

  const millis = timestampToMillis(value);
  if (typeof millis === 'number' && Number.isFinite(millis)) {
    return { __type: 'Instant', ms: millis };
  }

  if (value instanceof Set) {
    const normalizedValues = Array.from(value, (item) => stableSerialize(item));
    normalizedValues.sort((a, b) => {
      const aStr = JSON.stringify(a) ?? String(a);
      const bStr = JSON.stringify(b) ?? String(b);
      return aStr.localeCompare(bStr);
    });
    return { __type: 'Set', values: normalizedValues };
  }

  if (value instanceof Map) {
    const normalizedEntries = Array.from(value.entries()).map(([key, val]) => [
      stableSerialize(key),
      stableSerialize(val)
    ]);
    normalizedEntries.sort((a, b) => {
      const aStr = JSON.stringify(a[0]) ?? String(a[0]);
      const bStr = JSON.stringify(b[0]) ?? String(b[0]);
      return aStr.localeCompare(bStr);
    });
    return { __type: 'Map', entries: normalizedEntries };
  }

  if (Array.isArray(value)) {
    return value.map(stableSerialize);
  }

  const keys = Object.keys(value);
  if (keys.length === 0 && typeof value.valueOf === 'function') {
    const primitive = value.valueOf();
    if (primitive !== value) {
      return stableSerialize(primitive);
    }
  }

  return keys
    .sort()
    .reduce((acc, key) => {
      const next = stableSerialize(value[key]);
      if (next !== undefined) {
        acc[key] = next;
      }
      return acc;
    }, {});
}

/**
 * useRecordList - Edge-Indexed list hook
 *
 * Subscribes to view-scoped edges with a dynamically sized live window.
 * The window expands as the user paginates so every visible record keeps
 * realtime coverage, eliminating stale entries after remote deletions.
 *
 * Architecture:
 * - Live subscription for the loaded window of edges (limit grows on demand)
 * - Non-live pagination only used for search results
 * - Edges stored separately from entities (single source of truth)
 * - Entities derived from cache on each render
 * - Lazy hydration triggered by edge snapshots and cache changes
 *
 * Key principles:
 * - `items` is always the authoritative ordered edge list; entities are overlays.
 * - Extending the list simply bumps the live query limit instead of mixing live/non-live pages.
 * - Search runs in a separate code path because Algolia already owns ordering/pagination.
 * - The entity cache remains the SSOT for hydrated records; we never mutate `items` with entities.
 *
 * @param {Object} options - Hook options
 * @param {string} options.viewType - View type (note, task, calendar, etc.)
 * @param {Object} options.scope - Scope filter for edges
 * @param {{key: string, dir: 'asc'|'desc'}} [options.sortSpec={key: 'updatedAt', dir: 'desc'}] - Sort specification
 * @param {string} [options.searchTerm] - Optional search term
 * @param {number} [options.pageSize=30] - Number of edges per page / live window increment
 * @param {{key: string, start?: Date, end?: Date}|null} [options.range=null] - Optional range filter (start inclusive, end exclusive)
 */
export default function useRecordList({
  viewType,
  scope,
  sortSpec = { key: 'updatedAt', dir: 'desc' },
  searchTerm,
  pageSize = 30,
  range = null
}) {
  const { user } = useUser();
  const uid = user?.uid;

  const [items, setItems] = useState([]);
  const [liveLimit, setLiveLimit] = useState(pageSize);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchCursor, setSearchCursor] = useState(null);

  const unsubRef = useRef(null);
  const querySignatureRef = useRef(null);

  const byId = useEntityCache((s) => s.byId);
  const getOrFetchMany = useEntityCache((s) => s.getOrFetchMany);

  const sortKey = sortSpec?.key ?? 'updatedAt';
  const sortDir = sortSpec?.dir === 'asc' ? 'asc' : 'desc';
  const normalizedSortSpec = useMemo(
    () => ({ key: sortKey, dir: sortDir }),
    [sortKey, sortDir]
  );

  const trimmedSearchTerm = useMemo(
    () => (searchTerm ? searchTerm.trim() : ''),
    [searchTerm]
  );

  const normalizedRange = useMemo(() => {
    if (!range || (!range.start && !range.end)) return null;
    return {
      key: range.key ?? normalizedSortSpec.key,
      start: range.start ?? null,
      end: range.end ?? null
    };
  }, [range, normalizedSortSpec]);

  const querySignature = useMemo(
    () =>
      JSON.stringify(
        stableSerialize({
          uid: uid ?? null,
          viewType: viewType ?? null,
          scope: scope ?? null,
          sort: normalizedSortSpec,
          search: trimmedSearchTerm || '',
          pageSize,
          range: trimmedSearchTerm ? null : normalizedRange
        })
      ),
    [uid, viewType, scope, normalizedSortSpec, trimmedSearchTerm, pageSize, normalizedRange]
  );

  const latestQueryParamsRef = useRef({
    viewType,
    scope,
    sortSpec: normalizedSortSpec,
    pageSize,
    range: normalizedRange
  });

  useEffect(() => {
    latestQueryParamsRef.current = {
      viewType,
      scope,
      sortSpec: normalizedSortSpec,
      pageSize,
      range: normalizedRange
    };
  }, [viewType, scope, normalizedSortSpec, pageSize, normalizedRange]);

  useEffect(() => {
    // Reset list state and subscription only when the semantic query inputs truly change.
    // This avoids tearing down the listener for referentially new-but-equal filters.
    if (querySignatureRef.current === querySignature) {
      return;
    }

    querySignatureRef.current = querySignature;

    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    setItems([]);
    setHasMore(true);
    setError(null);
    setLoading(Boolean(uid));
    setLoadingMore(false);
    setSearchCursor(null);
    setLiveLimit(pageSize);
  }, [querySignature, uid, pageSize]);

  useEffect(() => {
    if (!uid) {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
      setLoading(false);
      setLoadingMore(false);
      setHasMore(false);
      return;
    }

    const {
      viewType: currentViewType,
      scope: currentScope,
      sortSpec: currentSortSpec,
      pageSize: currentPageSize,
      range: currentRange
    } = latestQueryParamsRef.current;

    if (trimmedSearchTerm) {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }

      let cancelled = false;
      setLoading(true);
      setError(null);

      (async () => {
        try {
          const res = await searchEdges({
            uid,
            viewType: currentViewType,
            scope: currentScope,
            searchTerm: trimmedSearchTerm,
            pageSize: currentPageSize,
            cursor: null
          });

          if (cancelled) return;

          setItems(
            res.items.map((i) => ({
              id: i.id,
              preview: i.preview,
              edge: i.edge
            }))
          );
          setHasMore(res.hasMore);
          setSearchCursor(res.nextCursor || null);
        } catch (err) {
          if (!cancelled) {
            console.error('Search failed:', err);
            setError(err);
            setItems([]);
            setHasMore(false);
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
            setLoadingMore(false);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    const rangeActive = Boolean(currentRange && currentRange.key && (currentRange.start || currentRange.end));
    const liveLimitValue = Math.max(1, liveLimit || currentPageSize || 1);
    const effectiveLimit = rangeActive ? null : liveLimitValue;

    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    setError(null);

    try {
      const unsub = subscribeEdges(
        {
          uid,
          viewType: currentViewType,
          scope: currentScope,
          sortSpec: currentSortSpec,
          pageSize: effectiveLimit,
          range: rangeActive ? currentRange : null,
          cursor: null
        },
        () => {},
        (snapshot) => {
          const liveItems = snapshot.docs.map((doc) => {
            const edge = doc.data();
            return {
              id: edge.recordId,
              preview: edge.preview,
              query: edge.query,
              edge,
              cursor: doc
            };
          });

          setItems(liveItems);
          setHasMore(rangeActive ? false : snapshot.size === liveLimitValue);
          setLoading(false);
          setLoadingMore(false);
        }
      );

      unsubRef.current = unsub;
    } catch (err) {
      console.error('Subscription failed:', err);
      setError(err);
      setLoading(false);
      setLoadingMore(false);
    }

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [uid, querySignature, trimmedSearchTerm, liveLimit]);

  const loadMore = useCallback(async () => {
    if (!uid || loading || loadingMore || !hasMore) return;

    if (!trimmedSearchTerm && normalizedRange) {
      return;
    }

    if (trimmedSearchTerm) {
      setLoadingMore(true);
      try {
        const res = await searchEdges({
          uid,
          viewType,
          scope,
          searchTerm: trimmedSearchTerm,
          pageSize,
          cursor: searchCursor
        });

        const appended = res.items.map((i) => ({
          id: i.id,
          preview: i.preview,
          edge: i.edge
        }));

        setItems((prev) => {
          const seen = new Set(prev.map((x) => x.id));
          const filtered = appended.filter((x) => !seen.has(x.id));
          return filtered.length ? [...prev, ...filtered] : prev;
        });
        setHasMore(res.hasMore);
        setSearchCursor(res.nextCursor || null);
      } catch (err) {
        console.error('Search pagination failed:', err);
        setError(err);
      } finally {
        setLoadingMore(false);
      }
      return;
    }

    setLoadingMore(true);
    setLiveLimit((prev) => prev + pageSize);
  }, [
    uid,
    viewType,
    scope,
    trimmedSearchTerm,
    normalizedRange,
    pageSize,
    searchCursor,
    loading,
    loadingMore,
    hasMore
  ]);

  useEffect(() => {
    // Keep a rolling window of hydrated entities for the first screenful so the UI feels instant.
    if (!items.length) return;

    const now = Date.now();
    const firstIds = items.slice(0, 15).map((i) => i.id);

    const misses = firstIds.filter((id) => {
      const entry = byId.get(id);
      return !entry || entry.expiresAt < now;
    });

    if (misses.length === 0) return;

    getOrFetchMany(misses, fetchRecordsByIds).catch((err) => {
      console.error('Entity hydration failed:', err);
    });
  }, [items, byId, getOrFetchMany]);

  const buildPreviewRecord = useCallback((item) => {
    // The edge projection provides enough data for a lightweight card; we fall back to it whenever
    // the entity cache misses or is stale so list rendering never blocks on full hydrations.
    const edge = item.edge ?? {
      recordId: item.id,
      ownerId: item.ownerId,
      preview: item.preview,
      query: item.query,
      viewFlags: item.viewFlags
    };

    const preview = edge.preview ?? {};
    const edgeQuery = edge.query ?? {};
    const viewFlags = edge.viewFlags ?? {};

    const attributes = {};

    const previewLinks = Array.isArray(preview.links)
      ? preview.links.filter((link) => link && link.targetId && link.targetType)
      : [];

    if (edgeQuery.isComplete !== undefined) {
      attributes.isComplete = edgeQuery.isComplete;
    }

    if (edgeQuery.dueAt) {
      attributes.dueDate = edgeQuery.dueAt;
    }

    const priority = normalizePriority(edgeQuery.priority);
    if (priority) {
      attributes.priority = priority;
    }

    const dateAttribute = buildDateAttributeFromQuery(edgeQuery);
    if (dateAttribute) {
      attributes.date = dateAttribute;
    }

    const locationAttribute = buildLocationAttributeFromQuery(edgeQuery);
    if (locationAttribute) {
      attributes.location = locationAttribute;
    }

    return {
      id: edge.recordId ?? item.id,
      access: {
        owner: edge.ownerId ?? null
      },
      content: {
        title: preview.title ?? '',
        body: preview.body ?? '',
        tags: preview.tags ?? [],
        attachmentsSummary: preview.attachments
          ? {
              count: preview.attachments.count,
              previews: preview.attachments.previews ?? []
            }
          : null
      },
      attributes,
      viewFlags: { ...viewFlags },
      metadata: {
        updatedAt: edgeQuery.recordUpdatedAt ?? edge.updatedAt ?? null
      },
      links: {
        outgoing: previewLinks.map((link) => ({
          targetId: link.targetId,
          targetType: link.targetType
        }))
      },
      // Mark as preview-only so UI can handle differently if needed
      _isPreview: true
    };
  }, []);

  const isCacheEntryFresh = useCallback((entry, item) => {
    if (!entry?.entity) return false;

    const edgeQueryTs = item.edge?.query ?? item.query ?? {};
    const edgeUpdatedAt = timestampToMillis(
      item.edge?.updatedAt ?? edgeQueryTs?.recordUpdatedAt
    );
    const entityUpdatedAt = timestampToMillis(entry.entity?.metadata?.updatedAt);

    if (edgeUpdatedAt && entityUpdatedAt) {
      return entityUpdatedAt >= edgeUpdatedAt;
    }

    return true;
  }, []);

  const records = useMemo(() => {
    const now = Date.now();
    return items.map((item) => {
      const entry = byId.get(item.id);
      if (entry && entry.expiresAt > now && isCacheEntryFresh(entry, item)) {
        return entry.entity;
      }

      return buildPreviewRecord(item);
    });
  }, [items, byId, buildPreviewRecord, isCacheEntryFresh]);

  const hydrate = useCallback(
    async (ids) => {
      const normalized = Array.isArray(ids) ? ids : [ids];
      if (!normalized.length) return Array.isArray(ids) ? [] : null;

      const entities = await getOrFetchMany(normalized, fetchRecordsByIds);
      if (Array.isArray(ids)) {
        return entities;
      }
      return entities.find((entity) => entity?.id === normalized[0]) ?? null;
    },
    [getOrFetchMany]
  );

  return {
    records,
    loading,
    loadingMore,
    error,
    hasMore,
    fetchNextPage: loadMore,
    hasNextPage: hasMore,
    refetch: () => {
      setItems((prev) => [...prev]);
    },
    isRefetching: false,
    isFetching: loading,
    hydrate,
    totalItems: items.length,
    hydrated: (() => {
      const now = Date.now();
      return items.filter((i) => {
        const entry = byId.get(i.id);
        return entry && entry.expiresAt > now && isCacheEntryFresh(entry, i);
      }).length;
    })()
  };
}
